import type { ApprovingInvoiceReportTransactionsResult } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useApprovingInvoiceReportTransactions } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useCallback, useEffect, useRef, useState } from 'react';

// `ap-openapi` types are only allowed inside `context/services/` — derive the
// row type from the service's own exported shape instead of importing
// `InvoiceTransactionDetail` directly here.
type InvoiceTransactionDetail =
  ApprovingInvoiceReportTransactionsResult['transactions'][number];

// Table layout's AC (NA-5530): show the first 10 records on open.
const INITIAL_LIMIT = 10;
// Background chunk size — AP's established default page size, not the
// endpoint's `maximum:1000` ceiling. See technical-design/ttd.md § Pagination
// strategy for the sourcing (`PaginationConcern::DEFAULT_PER_PAGE`,
// `useApprovingInvoiceReportsSearch`'s own `perPage` default).
const CHUNK_LIMIT = 50;

export type ApprovingInvoiceReportTransactionsPagedResult = {
  data: InvoiceTransactionDetail[];
  totalRecords: number;
  isExpanded: boolean;
  isLoadingMore: boolean;
  showRemaining: () => void;
  collapse: () => void;
};

/**
 * Expand/collapse + progressive-reveal orchestration for 支払明細's "show
 * remaining" (NA-5530). Wraps the thin data-fetching service hook
 * (`useApprovingInvoiceReportTransactions`) with the UI-facing pagination
 * state.
 *
 * A single 「残りをすべて表示」 click loads every remaining page, but the
 * requests run in the background one `CHUNK_LIMIT` (50) at a time and each
 * chunk is rendered the moment it arrives — the accumulated rows are revealed
 * while `isAutoFetching` is still true, so a 2,000-line report streams in
 * batches of 50 instead of showing nothing until all ~40 requests finish.
 * The infinite query caches fetched pages, so collapsing then re-expanding
 * reveals from memory without refetching.
 */
export const useApprovingInvoiceReportTransactionsPaged = (
  reportId: string
): ApprovingInvoiceReportTransactionsPagedResult => {
  const {
    transactions,
    totalRecords,
    hasNextPage,
    isFetchingNextPage,
    isError,
    fetchNextPage,
  } = useApprovingInvoiceReportTransactions(
    reportId,
    INITIAL_LIMIT,
    CHUNK_LIMIT
  );

  const [isExpanded, setIsExpanded] = useState(false);
  // Set once by `showRemaining`, then drives the auto-chaining effect below
  // until the last page comes back — a single click accumulates every
  // remaining page behind one loading state, without an imperative loop.
  const [isAutoFetching, setIsAutoFetching] = useState(false);

  // The drawer swaps `reportId` on the same mounted component rather than
  // remounting it, so this expand/collapse state must be reset per report —
  // the underlying infinite query is already keyed by `reportId` (fresh
  // pages, no stale data), but `isExpanded`/`isAutoFetching` are local UI
  // state and need their own reset. Reset synchronously during render (not a
  // `useEffect`) so there's no stale-expanded flash.
  const [seenReportId, setSeenReportId] = useState(reportId);
  // Report the auto-fetch chain is allowed to act on. Lets the effect below
  // reject a stray `fetchNextPage()` from a report already switched away
  // from (e.g. a request in flight when `reportId` changes).
  const chainReportIdRef = useRef(reportId);
  if (reportId !== seenReportId) {
    setSeenReportId(reportId);
    setIsExpanded(false);
    setIsAutoFetching(false);
    chainReportIdRef.current = reportId;
  }

  useEffect(() => {
    if (!isAutoFetching) return;
    if (chainReportIdRef.current !== reportId) return;
    // Stop the chain on error so it doesn't retry the same page forever, but
    // keep the already-fetched chunks revealed. Known limitation: `isError`
    // stays true until another fetch settles, so a retry click here won't
    // re-attempt until `reportId` changes.
    if (isError) {
      setIsAutoFetching(false);
      setIsExpanded(true);
      return;
    }
    // Last chunk is in: switch the button from its loading spinner to the
    // 「表示をたたむ」 (collapse) state.
    if (!hasNextPage) {
      setIsAutoFetching(false);
      setIsExpanded(true);
      return;
    }
    if (!isFetchingNextPage) fetchNextPage();
  }, [
    isAutoFetching,
    hasNextPage,
    isFetchingNextPage,
    isError,
    fetchNextPage,
    reportId,
  ]);

  const showRemaining = useCallback(() => {
    // The infinite query caches fetched pages, so re-expanding after a
    // collapse reveals from cache instead of refetching.
    if (!hasNextPage) {
      setIsExpanded(true);
      return;
    }
    // Kick off the background chain; rows stream in as each chunk lands
    // (see `data` below), while the button keeps its loading spinner until the
    // final chunk arrives.
    setIsAutoFetching(true);
  }, [hasNextPage]);

  const collapse = useCallback(() => {
    setIsExpanded(false);
    // Stop pulling further pages if the reviewer collapses mid-load.
    setIsAutoFetching(false);
  }, []);

  // Reveal all accumulated rows while the background chain is still running,
  // so batches of 50 stream in instead of appearing all at once at the end.
  const data =
    isExpanded || isAutoFetching
      ? transactions
      : transactions.slice(0, INITIAL_LIMIT);

  return {
    data,
    totalRecords,
    isExpanded,
    isLoadingMore: isAutoFetching,
    showRemaining,
    collapse,
  };
};
